import { GenericFoodEntity } from "../MealsAndDrinks/GenericFoodEntity"
import { CustomerEntity } from "../Customer/CustomerEntity"
import { Promotions } from "../Promotion/Promotions"
import { GenericOrderFoodAdditionalEntity } from "../Order/GenericOrderFoodAdditionalEntity"
import { getRestaurantNow } from "../../utils/timeZone"
import { EOrderItemChargeType } from "../../Enum/EOrderItemChargeType"

interface IAdditionalCalculatedValue {
  paidAmount: number
  freeAmount: number
  valueTotal: number
}

export class CalculatePromotions {
  customer: CustomerEntity

  valueDiscount: number = 0

  valueAddtionals: number = 0

  valueFinal: number = 0

  valueBase: number = 0

  mealsAndDrinks: GenericFoodEntity

  addtionals: GenericOrderFoodAdditionalEntity[]

  selections: { value?: number }[]

  hasPromotionAvaliable: boolean = false

  promotions: Promotions[]

  timeZoneId?: string

  chargeType: EOrderItemChargeType

  catalogValueSnapshot: number

  rodizioIncludesAdditionals: boolean

  constructor(data: {
    customer: CustomerEntity
    mealsAndDrinks: GenericFoodEntity
    addtionals: GenericOrderFoodAdditionalEntity[]
    selections?: { value?: number }[]
    timeZoneId?: string
    chargeType?: EOrderItemChargeType
    catalogValueSnapshot?: number
    rodizioIncludesAdditionals?: boolean
  }) {
    this.customer = data.customer

    this.valueFinal = 0

    this.addtionals = data.addtionals
    this.selections = data.selections ?? []

    this.mealsAndDrinks = data.mealsAndDrinks

    this.promotions = data.mealsAndDrinks.promotions
    this.timeZoneId = data.timeZoneId

    this.chargeType = data.chargeType ?? EOrderItemChargeType.Normal
    this.catalogValueSnapshot = data.catalogValueSnapshot ?? data.mealsAndDrinks.valueBase
    this.rodizioIncludesAdditionals = data.rodizioIncludesAdditionals ?? false

    this.calculate()
  }

  getValidPromotions(): Promotions[] {
    return this.promotions.filter((e) => e.isCanUse)
  }

  private getCalculatedValueForAdditional(
    item: GenericOrderFoodAdditionalEntity,
    index: number,
  ): IAdditionalCalculatedValue {
    if (item.isFree || item.valueUnique <= 0) {
      return {
        paidAmount: 0,
        freeAmount: item.amount,
        valueTotal: 0,
      }
    }

    const freeAllowedIds = this.mealsAndDrinks.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const hasFreeRule = (this.mealsAndDrinks.freeAddtionalsLimit ?? 0) > 0 && freeAllowedIds.length > 0

    if (!hasFreeRule || !freeAllowedIds.includes(item.addtionalEntityId)) {
      return {
        paidAmount: item.amount,
        freeAmount: 0,
        valueTotal: item.amount * item.valueUnique,
      }
    }

    let freeRemaining = Math.max(0, this.mealsAndDrinks.freeAddtionalsLimit ?? 0)

    for (let currentIndex = 0; currentIndex < index; currentIndex++) {
      const previous = this.addtionals[currentIndex]

      if (previous.valueUnique <= 0 && !previous.isFree) {
        continue
      }

      if (!freeAllowedIds.includes(previous.addtionalEntityId)) {
        continue
      }

      freeRemaining -= Math.min(freeRemaining, previous.amount)

      if (freeRemaining <= 0) {
        freeRemaining = 0
        break
      }
    }

    const freeAmount = Math.min(item.amount, freeRemaining)

    const paidAmount = item.amount - freeAmount

    return {
      paidAmount,
      freeAmount,
      valueTotal: paidAmount * item.valueUnique,
    }
  }

  getDisplayValueByAdditional(item: GenericOrderFoodAdditionalEntity, index: number): IAdditionalCalculatedValue {
    return this.getCalculatedValueForAdditional(item, index)
  }

  calculate() {
    const now = getRestaurantNow(this.timeZoneId)

    this.valueDiscount = 0

    this.valueFinal = 0

    this.valueAddtionals = 0

    this.hasPromotionAvaliable = false

    this.addtionals.forEach((e, index) => {
      this.valueAddtionals += this.getCalculatedValueForAdditional(e, index).valueTotal
    })

    this.selections.forEach((selection) => {
      this.valueAddtionals += Number(selection.value ?? 0)
    })

    if (this.chargeType === EOrderItemChargeType.RodizioAccess) {
      this.valueBase = this.catalogValueSnapshot

      this.valueFinal = this.catalogValueSnapshot + this.valueAddtionals

      return
    }

    if (this.chargeType === EOrderItemChargeType.RodizioIncluded) {
      if (this.rodizioIncludesAdditionals) {
        this.valueAddtionals = 0
      }

      this.valueBase = 0

      this.valueFinal = this.valueAddtionals

      return
    }

    this.valueBase = this.mealsAndDrinks.valueBase

    this.valueFinal = this.mealsAndDrinks.valueBase + this.valueAddtionals

    this.promotions.forEach((promo) => {
      const discount = promo.getDiscount(
        now,

        this.customer.personEntity.genderType,

        this.customer.oldType,

        this.valueFinal,
      )

      if (discount > 0) {
        this.hasPromotionAvaliable = true

        promo.isCanUse = true

        this.valueDiscount += discount

        this.valueFinal -= discount
      } else {
        promo.isCanUse = false
      }
    })
  }
}
